import { AlertCircle, InfoIcon, ExternalLink } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card'
import { Input } from '../../../ui/input'
import { Label } from '../../../ui/label'
import { Textarea } from '../../../ui/textarea'
import { CategorySelector } from '../../category-selector'
import { CountrySelector } from '../../country-selector'
import {
  REQUEST_BESPOKE_VALUE,
  LOCKDROPS_VALUE,
  SALES_TYPE_OPTIONS_FILTERED,
} from '../../../../lib/constants'
import {
  getCharacterHelperText as getCharacterHelper,
  getCharacterLengthError as getCharacterError,
  getCharacterCounterColor as getCounterColor,
} from '../../../../lib/validators'
import { DropdownSelect } from '../../../ui/dropdown-select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../ui/tooltip'

const DEFAULT_IF_GEO_BLOCKED_COUNTRIES = [
  'Afghanistan',
  'Burundi',
  'Burkina Faso',
  'Bosnia and Herzegovina',
  'Belarus',
  'Central African Republic',
  'China',
  'Ivory Coast',
  'DR Congo',
  'Cuba',
  'Cyprus',
  'Eritrea',
  'Fiji',
  'Guinea',
  'Guinea-Bissau',
  'Haiti',
  'Iran',
  'Iraq',
  'Laos',
  'Lebanon',
  'Liberia',
  'Libya',
  'Mali',
  'Myanmar',
  'Nicaragua',
  'North Korea',
  'Palestine',
  'Panama',
  'Rwanda',
  'Sudan',
  'Senegal',
  'Somalia',
  'South Sudan',
  'Syria',
  'Turkmenistan',
  'Uganda',
  'United States',
  'Venezuela',
  'Yemen',
  'Zimbabwe',
] as const

const jurisdictionOptions: { value: string; label: string }[] = [
  { value: 'united_states', label: 'United States' },
  { value: 'united_kingdom', label: 'United Kingdom' },
  { value: 'canada', label: 'Canada' },
  { value: 'australia', label: 'Australia' },
  { value: 'singapore', label: 'Singapore' },
  { value: 'switzerland', label: 'Switzerland' },
  { value: 'hong_kong', label: 'Hong Kong' },
  { value: 'japan', label: 'Japan' },
  { value: 'south_korea', label: 'South Korea' },
  { value: 'germany', label: 'Germany' },
  { value: 'france', label: 'France' },
  { value: 'netherlands', label: 'Netherlands' },
  { value: 'luxembourg', label: 'Luxembourg' },
  { value: 'liechtenstein', label: 'Liechtenstein' },
  { value: 'malta', label: 'Malta' },
  { value: 'estonia', label: 'Estonia' },
  { value: 'lithuania', label: 'Lithuania' },
  { value: 'cayman_islands', label: 'Cayman Islands' },
  { value: 'british_virgin_islands', label: 'British Virgin Islands' },
  { value: 'bermuda', label: 'Bermuda' },
  { value: 'bahamas', label: 'Bahamas' },
  { value: 'seychelles', label: 'Seychelles' },
  { value: 'mauritius', label: 'Mauritius' },
  { value: 'panama', label: 'Panama' },
  { value: 'delaware', label: 'Delaware (US)' },
  { value: 'wyoming', label: 'Wyoming (US)' },
  { value: 'dubai_uae', label: 'Dubai, UAE' },
  { value: 'abu_dhabi_uae', label: 'Abu Dhabi, UAE' },
  { value: 'new_zealand', label: 'New Zealand' },
  { value: 'ireland', label: 'Ireland' },
  { value: 'spain', label: 'Spain' },
  { value: 'italy', label: 'Italy' },
  { value: 'portugal', label: 'Portugal' },
  { value: 'sweden', label: 'Sweden' },
  { value: 'norway', label: 'Norway' },
  { value: 'denmark', label: 'Denmark' },
  { value: 'finland', label: 'Finland' },
  { value: 'iceland', label: 'Iceland' },
  { value: 'belgium', label: 'Belgium' },
  { value: 'austria', label: 'Austria' },
  { value: 'czech_republic', label: 'Czech Republic' },
  { value: 'poland', label: 'Poland' },
  { value: 'thailand', label: 'Thailand' },
  { value: 'vietnam', label: 'Vietnam' },
  { value: 'indonesia', label: 'Indonesia' },
  { value: 'malaysia', label: 'Malaysia' },
  { value: 'philippines', label: 'Philippines' },
  { value: 'taiwan', label: 'Taiwan' },
  { value: 'india', label: 'India' },
  { value: 'israel', label: 'Israel' },
  { value: 'turkey', label: 'Turkey' },
  { value: 'south_africa', label: 'South Africa' },
  { value: 'brazil', label: 'Brazil' },
  { value: 'mexico', label: 'Mexico' },
  { value: 'argentina', label: 'Argentina' },
  { value: 'chile', label: 'Chile' },
  { value: 'colombia', label: 'Colombia' },
  { value: 'other', label: 'Other' },
]

interface VisibilityEligibilitySectionProps {
  localData: {
    salesType?: string
    salesTypeDetail?: string
  }
  errors: Record<string, string>
  updateField: (field: string, value: any) => void
  requestChanges?: Record<string, string>
}

export function VisibilityEligibilitySection({
  localData,
  errors,
  updateField,
  requestChanges,
}: VisibilityEligibilitySectionProps) {
  const selectedSalesType = ((localData as any).salesType || '') as string
  const salesTypeDetailValue = ((localData as any).salesTypeDetail ||
    '') as string

  const handleSalesTypeChange = (value: string) => {
    if (value === selectedSalesType) return
    updateField('salesType', value)
    if (value === REQUEST_BESPOKE_VALUE || value === LOCKDROPS_VALUE) {
      if (salesTypeDetailValue) updateField('salesTypeDetail', '')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Visibility & Eligibility</CardTitle>
        <p className="text-sm text-neutral-600">
          Choose the required sale format, or request bespoke.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2" id="salesTypeDetail">
          <Label>Type of Deal</Label>
          <DropdownSelect
            value={selectedSalesType}
            onChange={handleSalesTypeChange}
            options={SALES_TYPE_OPTIONS_FILTERED.map((option) => ({
              value: option.value,
              label: option.label,
              description: option.description,
            }))}
            placeholder="Select sales type"
            error={(errors as any).salesType}
            ariaLabel="Select sales type"
          />
          {selectedSalesType === REQUEST_BESPOKE_VALUE && (
            <div className="space-y-1">
              <Textarea
                id="salesTypeCustom"
                value={salesTypeDetailValue}
                onChange={(e) => updateField('salesTypeDetail', e.target.value)}
                placeholder="Please specify the type of deal"
                className={`${
                  (errors as any).salesTypeDetail ? 'border-red-500' : ''
                } min-h-[80px]`}
              />
              {(errors as any).salesTypeDetail && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {(errors as any).salesTypeDetail}
                </p>
              )}
              {requestChanges?.salesTypeDetail && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges.salesTypeDetail}
                </p>
              )}
            </div>
          )}
          {selectedSalesType === 'OTC Deal' && (
            <div className="space-y-1">
              <Textarea
                id="otcDealType"
                value={salesTypeDetailValue}
                onChange={(e) => updateField('salesTypeDetail', e.target.value)}
                placeholder="Please specify the type of OTC deal (e.g., LP provisioning, TVL bootstrapping, structured placement)"
                className={`${
                  (errors as any).salesTypeDetail ? 'border-red-500' : ''
                } min-h-[80px]`}
              />
              {(errors as any).salesTypeDetail && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {(errors as any).salesTypeDetail}
                </p>
              )}
              {requestChanges?.salesTypeDetail && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges.salesTypeDetail}
                </p>
              )}
            </div>
          )}
          {selectedSalesType === LOCKDROPS_VALUE && (
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <span className="text-sm text-neutral-700">
                  Summarize the Lockdrop&apos;s proposal and allocation
                  strategy.
                </span>
                <a
                  href="https://www.notion.so/impossiblefinance/Lockdrop-deal-terms-summary-example-2d27cf9e26b88003862dc31939ada48e"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-brand-primary hover:text-brand-hover inline-flex items-center gap-1"
                >
                  See example
                  <ExternalLink className="w-4 h-4" strokeWidth={2.5} />
                </a>
              </div>
              <Textarea
                id="lockdropDescription"
                value={salesTypeDetailValue}
                onChange={(e) => updateField('salesTypeDetail', e.target.value)}
                placeholder="Please specify the type of lockdrop deal"
                className={`${
                  (errors as any).salesTypeDetail ? 'border-red-500' : ''
                } min-h-[80px]`}
              />
              {(errors as any).salesTypeDetail && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {(errors as any).salesTypeDetail}
                </p>
              )}
              {requestChanges?.salesTypeDetail && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges.salesTypeDetail}
                </p>
              )}
            </div>
          )}
          {requestChanges?.salesType && (
            <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
              <AlertCircle className="w-3 h-3" />
              {requestChanges.salesType}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

interface OverviewBasicsSectionProps {
  headingId?: string
  localData: {
    projectName: string
    category: string[]
    jurisdiction: string
    jurisdictionOther?: string
    countryRestrictions: string[]
    salesType?: string
    salesTypeDetail?: string
  }
  errors: Record<string, string>
  openDropdownId: string | null
  setOpenDropdownId: (id: string | null) => void
  updateField: (field: string, value: any) => void
  requestChanges?: Record<string, string>
}

export function OverviewBasicsSection({
  headingId,
  localData,
  errors,
  openDropdownId,
  setOpenDropdownId,
  updateField,
  requestChanges,
}: OverviewBasicsSectionProps) {
  const jurisdictionOtherValue = ((localData as any).jurisdictionOther ||
    '') as string

  const handleJurisdictionChange = (value: string) => {
    if (value === localData.jurisdiction) return
    updateField('jurisdiction', value)
    if (value !== 'other' && jurisdictionOtherValue)
      updateField('jurisdictionOther', '')
  }

  const isJurisdictionOther = localData.jurisdiction === 'other'

  return (
    <Card>
      <CardHeader>
        <CardTitle>Basic Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="projectName">Project Name</Label>
          <Input
            id="projectName"
            value={localData.projectName}
            onChange={(e) => updateField('projectName', e.target.value)}
            placeholder="Enter project name"
            maxLength={40}
            className={`${
              errors.projectName ? 'border-red-500 bg-red-50' : ''
            }`}
          />
          <div className="flex justify-between items-start">
            <div className="flex-1 flex flex-col gap-1">
              {getCharacterError(
                localData.projectName.length,
                40,
                'projectName',
              ) && (
                <p className="text-sm text-status-error inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {getCharacterError(
                    localData.projectName.length,
                    40,
                    'projectName',
                  )}
                </p>
              )}
              {getCharacterHelper(localData.projectName.length, 40) && (
                <p className="text-sm text-status-warning">
                  {getCharacterHelper(localData.projectName.length, 40)}
                </p>
              )}
              {errors.projectName &&
                !getCharacterError(
                  localData.projectName.length,
                  40,
                  'projectName',
                ) && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.projectName}
                  </p>
                )}
              {requestChanges?.projectName && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges.projectName}
                </p>
              )}
            </div>
            <span
              className={`text-sm ml-2 flex-shrink-0 ${getCounterColor(
                localData.projectName.length,
                40,
              )}`}
            >
              {localData.projectName.length}/40
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Tags (optional)</Label>
          <p className="text-xs mb-2 text-neutral-600">
            Select up to 3 tags. You can also add custom tags
          </p>
          <CategorySelector
            selectedCategories={localData.category}
            onCategoriesChange={(c) => updateField('category', c)}
            maxCategories={3}
          />
        </div>

        {localData.salesType !== 'OTC Deal' && (
          <>
            <div className="border-t border-neutral-200"></div>

            <div className="space-y-2" id="jurisdiction">
              <Label>Jurisdiction</Label>
              <p className="text-xs text-neutral-500 mb-2">
                Enter the legal jurisdiction where this project or sale is
                registered
              </p>
              <DropdownSelect
                value={localData.jurisdiction}
                onChange={handleJurisdictionChange}
                options={jurisdictionOptions}
                placeholder="Select jurisdiction"
                error={errors.jurisdiction}
                ariaLabel="Select jurisdiction"
              />
              {requestChanges?.jurisdiction && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges.jurisdiction}
                </p>
              )}
              {isJurisdictionOther && (
                <div className="space-y-2">
                  <Label htmlFor="jurisdictionOther">
                    Specify Jurisdiction
                  </Label>
                  <Input
                    id="jurisdictionOther"
                    type="text"
                    placeholder="Enter jurisdiction"
                    value={jurisdictionOtherValue}
                    onChange={(e) =>
                      updateField('jurisdictionOther', e.target.value)
                    }
                    className={`${
                      errors.jurisdictionOther ? 'border-red-500 bg-red-50' : ''
                    }`}
                  />
                  <div className="flex flex-col gap-1">
                    {errors.jurisdictionOther && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.jurisdictionOther}
                      </p>
                    )}
                    {requestChanges?.jurisdictionOther && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.jurisdictionOther}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="border-t border-neutral-200"></div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="countryRestrictions">
              Country / Area Restrictions (optional)
            </Label>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="View IF default geo-blocked countries"
                    className="text-neutral-500 hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-400 rounded-full p-1 -m-1"
                  >
                    <InfoIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  sideOffset={8}
                  className="max-w-[340px] rounded-md bg-white px-4 py-3 text-xs text-neutral-900 shadow-lg border border-neutral-200"
                >
                  <p className="text-[11px] text-neutral-700 leading-snug">
                    CURATED reviews and updates our list of restricted
                    jurisdictions on an ongoing basis. The jurisdictions
                    currently restricted (subject to change without prior
                    notice) are:
                  </p>
                  <p className="mt-2 text-[11px] text-neutral-900 leading-snug">
                    {DEFAULT_IF_GEO_BLOCKED_COUNTRIES.join(', ')}.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-xs text-neutral-500 mb-2">
            CURATED already restricts certain jurisdictions by default. Add any
            additional countries or areas that should be restricted from
            participating in this deal.
          </p>
          <CountrySelector
            selectedCountries={localData.countryRestrictions}
            onCountriesChange={(c) => updateField('countryRestrictions', c)}
          />
          <div className="flex flex-col gap-1">
            {requestChanges?.countryRestrictions && (
              <p className="text-status-error text-xs inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {requestChanges.countryRestrictions}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
